import { useEffect, useState } from "react";
import { styles } from "../styles";
import { PAYMENT_METHOD_OPTIONS } from "../models/models";

const money = (n) =>
  (Number(n) || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

// Reusable payment-entry modal — captures one payment installment
// (amount + method + date + note). Used on both sides of the Finance
// module: AR (recording a customer payment on a finalized bill) and AP
// (recording a vendor payment on a verified PO bill).
//
// Same component / same shape on both sides so the bookkeeper's muscle
// memory transfers between AR and AP. Caller persists the result.
//
// Props:
//   open           — boolean; renders nothing when false
//   title          — modal heading (e.g. "Record customer payment")
//   subtitle       — small line below the title (invoice # / vendor name)
//   billTotal      — full bill total (display only)
//   alreadyPaid    — running total of prior payments (display only)
//   onConfirm      — async ({ amount, method, date, note }) => void
//   onCancel       — () => void
export default function PaymentEntryModal({
  open,
  title = "Record payment",
  subtitle = "",
  billTotal = 0,
  alreadyPaid = 0,
  onConfirm,
  onCancel,
}) {
  const remaining = Math.max(0, (Number(billTotal) || 0) - (Number(alreadyPaid) || 0));
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("check");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset state every time the modal opens so a previously-canceled
  // entry doesn't bleed into the next attempt.
  useEffect(() => {
    if (open) {
      setAmount(remaining > 0 ? remaining.toFixed(2) : "");
      setMethod("check");
      setDate(new Date().toISOString().slice(0, 10));
      setNote("");
      setBusy(false);
    }
  }, [open, remaining]);

  if (!open) return null;

  const amt = parseFloat(amount) || 0;
  const overpay = amt > remaining + 0.005;

  const submit = async () => {
    if (amt <= 0) return;
    setBusy(true);
    try {
      await onConfirm({
        amount: amt,
        method,
        date,
        note: note.trim(),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      onClick={() => !busy && onCancel?.()}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
        padding: "16px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "white",
          borderRadius: "10px",
          padding: "20px",
          width: "100%",
          maxWidth: "400px",
          boxShadow: "0 10px 32px rgba(0,0,0,0.3)",
        }}
      >
        <h3 style={{ margin: "0 0 4px" }}>{title}</h3>
        {subtitle && (
          <div style={{ fontSize: "12px", color: "#666", marginBottom: "8px" }}>
            {subtitle}
          </div>
        )}
        <div
          style={{
            fontSize: "12px",
            color: "#444",
            padding: "6px 8px",
            background: "#f3f4f6",
            borderRadius: "4px",
            marginBottom: "12px",
          }}
        >
          Bill total: <strong>{money(billTotal)}</strong>
          {alreadyPaid > 0 && (
            <>
              {" "}
              · Paid so far: <strong>{money(alreadyPaid)}</strong>
            </>
          )}
          {" "}· Remaining: <strong>{money(remaining)}</strong>
        </div>
        <div style={{ marginBottom: "8px" }}>
          <label style={{ fontSize: "12px", fontWeight: 600 }}>Amount $</label>
          <br />
          <input
            type="number"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            style={{ ...styles.input, width: "100%", boxSizing: "border-box" }}
            placeholder="0.00"
          />
          {overpay && (
            <div
              style={{
                fontSize: "11px",
                color: "#a15c00",
                marginTop: "2px",
              }}
            >
              Note: this payment is larger than the remaining due
              ({money(remaining)}).
            </div>
          )}
        </div>
        <div style={{ marginBottom: "8px" }}>
          <label style={{ fontSize: "12px", fontWeight: 600 }}>Method</label>
          <br />
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            style={{ width: "100%" }}
          >
            {PAYMENT_METHOD_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: "8px" }}>
          <label style={{ fontSize: "12px", fontWeight: 600 }}>
            Payment date
          </label>
          <br />
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div style={{ marginBottom: "12px" }}>
          <label style={{ fontSize: "12px", fontWeight: 600 }}>
            Note (optional)
          </label>
          <br />
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            style={{ ...styles.input, width: "100%", boxSizing: "border-box" }}
            placeholder="Check # 4521, partial install payment, etc."
          />
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            style={{ ...styles.button, flex: 1 }}
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            style={{
              ...styles.button,
              flex: 1,
              background: "#1a7f37",
              color: "white",
              fontWeight: 600,
            }}
            onClick={submit}
            disabled={busy || amt <= 0}
          >
            {busy ? "Saving..." : "Record payment"}
          </button>
        </div>
      </div>
    </div>
  );
}
